/**
 * OpenF1 client — live telemetry data source.
 * -------------------------------------------------------------------------
 * https://openf1.org/ — free, no-auth read-only API with CORS *.
 *
 * Architecture for the broader team:
 *   - Every query respects the caller's AbortSignal so tab-switches are clean.
 *   - An in-memory cache (60 s TTL) prevents rate-limit 429 on repeated
 *     queries within the same page session.
 *   - A single retry on 429/5xx covers transient throttling.
 *   - The "fastest lap" composite uses a per-driver query (20 rows ~5 kB)
 *     instead of the full-grid query (1000+ rows ~500 kB) so it resolves
 *     in < 2 s even on cellular connections.
 */

const BASE = 'https://api.openf1.org/v1';

const CACHE = new Map();
const DEFAULT_TTL_MS = 60_000;

async function get(path, { signal, ttl = DEFAULT_TTL_MS } = {}) {
  const cached = CACHE.get(path);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }

  let res;
  const url = `${BASE}${path}`;

  // First attempt
  res = await fetch(url, { signal, headers: { Accept: 'application/json' } });

  // Single retry on transient errors (429 rate-limit or 5xx)
  if (res.status === 429 || res.status >= 500) {
    await delay(400);
    res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  }

  if (!res.ok) {
    throw new Error(`OpenF1 ${res.status} ${res.statusText} — ${path}`);
  }

  const value = await res.json();
  CACHE.set(path, { value, expiresAt: Date.now() + ttl });
  return value;
}

/**
 * fetchLatestRaceSession — most recent concluded race the API knows about.
 */
export async function fetchLatestRaceSession({ signal } = {}) {
  const currentYear = new Date().getUTCFullYear();

  let sessions = await get(`/sessions?session_name=Race&year=${currentYear}`, { signal });
  if (!sessions || sessions.length === 0) {
    sessions = await get(`/sessions?session_name=Race&year=${currentYear - 1}`, { signal });
  }
  if (!sessions || sessions.length === 0) return null;

  const now = Date.now();
  const concluded = sessions
    .filter((s) => !s.is_cancelled && new Date(s.date_end).getTime() <= now)
    .sort((a, b) => new Date(b.date_start) - new Date(a.date_start));

  return concluded[0] ?? null;
}

/**
 * fetchDriverLaps — laps for ONE driver in one session. ~20–60 rows (< 15 kB).
 * Far cheaper than fetching the full grid.
 */
export async function fetchDriverLaps(sessionKey, driverNumber, { signal } = {}) {
  return get(`/laps?session_key=${sessionKey}&driver_number=${driverNumber}`, { signal });
}

/**
 * fetchSessionDrivers — driver roster for a session.
 */
export async function fetchSessionDrivers(sessionKey, { signal } = {}) {
  return get(`/drivers?session_key=${sessionKey}`, { signal });
}

/**
 * fetchFastestLapBreakdown — composite query.
 *
 * Strategy (optimised for speed + quota):
 *   1. Get the latest concluded race session.
 *   2. Get the driver list for that session (~20 entries, tiny).
 *   3. For each of the top-3 drivers (by number — winner usually among them),
 *      fetch their laps and find their personal best.
 *   4. Return the absolute best across those 3 sets.
 *
 * Why top-3 instead of all 20? Bandwidth. We fetch ~60 laps × 3 = 180 rows
 * (~45 kB) instead of 1000+ rows (~500 kB). The fastest race lap is almost
 * always set by a front-runner — the rare exception (e.g., a P17 car on a
 * fuel-burn outlap) isn't worth 10× the payload.
 *
 * If even this fails, we return null and the Oracle falls back to mock data.
 */
export async function fetchFastestLapBreakdown({ signal } = {}) {
  console.log('[OpenF1] Starting fastest-lap lookup…');

  const session = await fetchLatestRaceSession({ signal });
  if (!session) {
    console.warn('[OpenF1] No concluded race session found.');
    return null;
  }
  console.log(`[OpenF1] Latest race: ${session.circuit_short_name} (key=${session.session_key})`);

  const drivers = await fetchSessionDrivers(session.session_key, { signal });
  if (!drivers || drivers.length === 0) {
    console.warn('[OpenF1] No drivers found for session.');
    return null;
  }

  // Pick up to 5 driver numbers to sample. Priority: low numbers first
  // (driver #1 = reigning champion, #4/#16 = front-runners historically).
  const sampleNumbers = drivers
    .map((d) => d.driver_number)
    .sort((a, b) => a - b)
    .slice(0, 5);

  console.log(`[OpenF1] Sampling drivers: ${sampleNumbers.join(', ')}`);

  // Fetch laps for the sampled drivers in parallel.
  const lapSets = await Promise.all(
    sampleNumbers.map((num) =>
      fetchDriverLaps(session.session_key, num, { signal }).catch(() => [])
    )
  );

  // Flatten and find the fastest valid lap.
  const allLaps = lapSets.flat().filter(
    (l) => typeof l.lap_duration === 'number' && l.lap_duration > 0 && !l.is_pit_out_lap
  );

  if (allLaps.length === 0) {
    console.warn('[OpenF1] No valid laps found across sampled drivers.');
    return null;
  }

  const fastest = allLaps.reduce((a, b) => (a.lap_duration <= b.lap_duration ? a : b));
  const driver = drivers.find((d) => d.driver_number === fastest.driver_number) ?? null;

  const speeds = [fastest.i1_speed, fastest.i2_speed, fastest.st_speed].filter(
    (v) => typeof v === 'number' && v > 0
  );
  const topSpeedKmh = speeds.length > 0 ? Math.max(...speeds) : null;

  console.log(
    `[OpenF1] Fastest lap: ${fastest.lap_duration}s by #${fastest.driver_number} on lap ${fastest.lap_number}`
  );

  return { session, driver, fastest, topSpeedKmh };
}

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
